import { useState } from 'react';
import { adminBlogService, CreateBlogPostRequest } from '@/services/blog';

interface Message {
  type: 'success' | 'error' | 'info';
  text: string;
}

interface UploadStatus {
  total: number;
  completed: number;
  failed: number;
  errors: string[];
}

export default function BlogUploadPage() {
  // Single Blog Creation
  const [singleBlogTitle, setSingleBlogTitle] = useState('');
  const [singleBlogSlug, setSingleBlogSlug] = useState('');
  const [singleBlogContent, setSingleBlogContent] = useState('');
  const [singleBlogAuthorName, setSingleBlogAuthorName] = useState('Admin');
  const [singleBlogAuthorEmail, setSingleBlogAuthorEmail] = useState('');
  const [isCreatingBlog, setIsCreatingBlog] = useState(false);
  const [singleBlogAutoPublish, setSingleBlogAutoPublish] = useState(true);
  const [singleCoverFile, setSingleCoverFile] = useState<File | null>(null);
  const [singleCoverUrl, setSingleCoverUrl] = useState('');
  const [isUploadingSingleCover, setIsUploadingSingleCover] = useState(false);

  // CSV Upload
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [csvAutoPublish, setCsvAutoPublish] = useState(true);

  // Messages
  const [messages, setMessages] = useState<Message[]>([]);

  const showMessage = (type: Message['type'], text: string) => {
    setMessages((prev) => [...prev, { type, text }]);
    setTimeout(() => setMessages((prev) => prev.slice(1)), 5000);
  };

  const createSingleBlog = async () => {
    const title = singleBlogTitle.trim();
    const content = singleBlogContent.trim();

    if (!title) {
      showMessage('error', 'Title is required');
      return;
    }
    if (title.length > 255) {
      showMessage('error', 'Title must be at most 255 characters');
      return;
    }
    if (!content) {
      showMessage('error', 'Content is required');
      return;
    }
    if (content.length < 10) {
      showMessage('error', `Content must be at least 10 characters (currently ${content.length})`);
      return;
    }

    setIsCreatingBlog(true);
    try {
      const request: CreateBlogPostRequest = {
        title: singleBlogTitle,
        slug: singleBlogSlug || undefined,
        content: singleBlogContent,
        author_name: singleBlogAuthorName.trim() || undefined,
        author_email: singleBlogAuthorEmail.trim() || undefined,
        featured_image: singleCoverUrl || undefined,
        auto_publish: singleBlogAutoPublish,
      };

      const response = await adminBlogService.createPost(request);

      if (response.success) {
        showMessage('success', response.message);
        setSingleBlogTitle('');
        setSingleBlogSlug('');
        setSingleBlogContent('');
        setSingleBlogAuthorName('Admin');
        setSingleBlogAuthorEmail('');
        setSingleCoverFile(null);
        setSingleCoverUrl('');
      }
    } catch (error: any) {
      showMessage('error', error.message || 'Failed to create blog post');
    } finally {
      setIsCreatingBlog(false);
    }
  };

  const uploadSingleCover = async () => {
    if (!singleCoverFile) {
      showMessage('error', 'Please select a cover image first');
      return;
    }
    setIsUploadingSingleCover(true);
    try {
      const res = await adminBlogService.uploadCovers([singleCoverFile]);
      if (res.success) {
        const first = (res.uploaded || []).find((u) => !u.error && u.url);
        if (first?.url) {
          setSingleCoverUrl(first.url);
          showMessage('success', 'Cover uploaded');
        } else {
          showMessage('error', 'Cover upload failed');
        }
      }
    } catch (error: any) {
      showMessage('error', error.message || 'Cover upload failed');
    } finally {
      setIsUploadingSingleCover(false);
    }
  };

  const parseCSV = (csvText: string): CreateBlogPostRequest[] => {
    const lines = csvText.split('\n').filter((line) => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    const titleIdx = headers.indexOf('title');
    const contentIdx = headers.indexOf('content');
    const slugIdx = headers.indexOf('slug');
    const authorNameIdx = headers.indexOf('author_name');
    const authorEmailIdx = headers.indexOf('author_email');

    if (titleIdx === -1 || contentIdx === -1) {
      throw new Error('CSV must contain "title" and "content" columns');
    }

    const posts: CreateBlogPostRequest[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',');
      const title = values[titleIdx]?.trim();
      const content = values[contentIdx]?.trim();

      if (title && content) {
        posts.push({
          title,
          content,
          slug: slugIdx >= 0 ? values[slugIdx]?.trim() || undefined : undefined,
          author_name: authorNameIdx >= 0 ? values[authorNameIdx]?.trim() || 'Admin' : 'Admin',
          author_email: authorEmailIdx >= 0 ? values[authorEmailIdx]?.trim() || undefined : undefined,
          auto_publish: csvAutoPublish,
        });
      }
    }

    return posts;
  };

  const handleCSVUpload = async () => {
    if (!csvFile) {
      showMessage('error', 'Please select a CSV file');
      return;
    }

    setIsUploading(true);
    setUploadStatus(null);
    setUploadProgress(0);

    try {
      const text = await csvFile.text();
      const posts = parseCSV(text);

      if (posts.length === 0) {
        showMessage('error', 'No valid posts found in CSV');
        setIsUploading(false);
        return;
      }

      showMessage('info', `Found ${posts.length} posts. Starting upload...`);

      const batchSize = 5;
      const batches: CreateBlogPostRequest[][] = [];
      for (let i = 0; i < posts.length; i += batchSize) {
        batches.push(posts.slice(i, i + batchSize));
      }

      let totalCompleted = 0;
      let totalFailed = 0;
      const errors: string[] = [];

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        try {
          const response = await adminBlogService.batchCreatePosts({
            posts: batch,
            auto_publish: csvAutoPublish,
          });

          totalCompleted += response.created_count;
          totalFailed += response.failed_count;

          if (response.failed_posts?.length > 0) {
            response.failed_posts.forEach((fp) => {
              errors.push(`${fp.title}: ${fp.error}`);
            });
          }

          setUploadProgress(((batchIndex + 1) / batches.length) * 100);
        } catch (error: any) {
          errors.push(`Batch ${batchIndex + 1} failed: ${error.message}`);
          totalFailed += batch.length;
        }

        if (batchIndex < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      setUploadStatus({
        total: posts.length,
        completed: totalCompleted,
        failed: totalFailed,
        errors,
      });

      if (totalFailed === 0) {
        showMessage('success', `Successfully created all ${totalCompleted} blog posts!`);
      } else {
        showMessage('error', `Created ${totalCompleted} posts, ${totalFailed} failed`);
      }
    } catch (error: any) {
      showMessage('error', error.message || 'Failed to parse CSV');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="admin-blog-upload">
      <div className="admin-blog-upload-header">
        <h1>Blog Upload</h1>
        <p className="admin-text-muted">Create single posts or batch upload via CSV</p>
      </div>

      {messages.length > 0 && (
        <div className="admin-messages">
          {messages.map((m, idx) => (
            <div key={idx} className={`admin-message admin-message--${m.type}`}>{m.text}</div>
          ))}
        </div>
      )}

      <div className="admin-blog-upload-content">
        {/* Single Post Creation */}
        <div className="admin-upload-card">
          <h2>Create Single Post</h2>

          <div className="form-group">
            <label>Title * <span className="char-count">{singleBlogTitle.length}/255</span></label>
            <input
              type="text"
              value={singleBlogTitle}
              onChange={(e) => setSingleBlogTitle(e.target.value)}
              placeholder="Enter post title"
              maxLength={255}
              className={singleBlogTitle.length > 255 ? 'input-error' : ''}
            />
            {singleBlogTitle.length > 0 && singleBlogTitle.length > 255 && (
              <span className="field-error">Title must be at most 255 characters</span>
            )}
          </div>

          <div className="form-group">
            <label>Slug (optional)</label>
            <input
              type="text"
              value={singleBlogSlug}
              onChange={(e) => setSingleBlogSlug(e.target.value)}
              placeholder="custom-url-slug"
            />
          </div>

          <div className="form-group">
            <label>
              Content * (Markdown supported)
              <span className={`char-count ${singleBlogContent.length > 0 && singleBlogContent.length < 10 ? 'char-count-error' : ''}`}>
                {singleBlogContent.length} characters {singleBlogContent.length < 10 ? '(min 10)' : ''}
              </span>
            </label>
            <textarea
              value={singleBlogContent}
              onChange={(e) => setSingleBlogContent(e.target.value)}
              placeholder="Write your blog content here... (minimum 10 characters)"
              rows={10}
              className={singleBlogContent.length > 0 && singleBlogContent.length < 10 ? 'input-error' : ''}
            />
            {singleBlogContent.length > 0 && singleBlogContent.length < 10 && (
              <span className="field-error">Content must be at least 10 characters</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Author Name</label>
              <input
                type="text"
                value={singleBlogAuthorName}
                onChange={(e) => setSingleBlogAuthorName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Author Email</label>
              <input
                type="email"
                value={singleBlogAuthorEmail}
                onChange={(e) => setSingleBlogAuthorEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Cover Image</label>
            <div className="cover-upload-row">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setSingleCoverFile(e.target.files?.[0] || null)}
              />
              <button
                onClick={uploadSingleCover}
                disabled={!singleCoverFile || isUploadingSingleCover}
              >
                {isUploadingSingleCover ? 'Uploading...' : 'Upload Cover'}
              </button>
            </div>
            {singleCoverUrl && (
              <div className="cover-preview">
                <img src={singleCoverUrl} alt="Cover preview" />
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={singleBlogAutoPublish}
                onChange={(e) => setSingleBlogAutoPublish(e.target.checked)}
              />
              Publish immediately
            </label>
          </div>

          <button
            className="admin-btn-primary"
            onClick={createSingleBlog}
            disabled={isCreatingBlog}
          >
            {isCreatingBlog ? 'Creating...' : 'Create Post'}
          </button>
        </div>

        {/* CSV Batch Upload */}
        <div className="admin-upload-card">
          <h2>CSV Batch Upload</h2>
          <p className="admin-text-muted admin-text-sm">
            CSV must contain "title" and "content" columns. Optional: slug, author_name, author_email
          </p>

          <div className="form-group">
            <label>Select CSV File</label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={csvAutoPublish}
                onChange={(e) => setCsvAutoPublish(e.target.checked)}
              />
              Publish all posts immediately
            </label>
          </div>

          {isUploading && (
            <div className="admin-upload-progress">
              <div className="progress-text">
                <span>Uploading...</span>
                <span>{Math.round(uploadProgress)}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          {uploadStatus && (
            <div className="upload-status">
              <p>Total: {uploadStatus.total} | Completed: {uploadStatus.completed} | Failed: {uploadStatus.failed}</p>
              {uploadStatus.errors.length > 0 && (
                <div className="upload-errors">
                  {uploadStatus.errors.map((err, idx) => (
                    <div key={idx} className="error-item">{err}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            className="admin-btn-primary"
            onClick={handleCSVUpload}
            disabled={isUploading || !csvFile}
          >
            {isUploading ? 'Uploading...' : 'Upload CSV'}
          </button>
        </div>
      </div>
    </div>
  );
}
